// Pure helpers for the review's ~1-minute voice quiz: deciding WHICH
// already-covered concept to quiz. Kept pure (a `randomFn` is injected) so the
// choice is deterministically unit-testable. When the quiz opens is decided by
// the marks on the progress bar (review-progress-marks/).

import type { CardData } from "../../constants/GlobalTypesAndEnumsAndConstants";
import { getAssessmentUnitPassageTextForTutor } from "../../utils/assessment-units";

/** Minimal card shape needed for concept selection. */
export interface CardWithConcept {
  card_key_concept_name?: string | null;
  assessment_unit_id?: string;
  assessment_unit_kind?: "independent" | "single_stimulus" | "shared_stimulus";
}

export interface CoveredConceptSelection {
  concept: string;
  sourceCardIndex: number;
}

export interface ExploredPassageQuestionContext {
  question: string;
  correctAnswer?: string;
  learnerAnswer?: string;
}

export interface CoveredPassageChallengeContext {
  passageContext?: string;
  exploredQuestions?: ExploredPassageQuestionContext[];
}

/**
 * The review-checkpoint quiz is one minute, billed against the weekly free voice
 * minutes. Shared by the modal (the hard call cap) and the review trigger (the
 * eligibility gate — only fire when the learner can afford this many seconds).
 */
export const REVIEW_CHECKPOINT_CHALLENGE_SECONDS = 60;

/**
 * Pick a random concept the learner has ALREADY covered this session — a
 * distinct, non-empty `card_key_concept_name` among the cards from the start of
 * the session up to and including the current card. Returns null if none of the
 * covered cards carry a concept name (then the caller skips the challenge).
 *
 * @param cardsData the session's cards in review order
 * @param indexOfCurrentCard 0-indexed position of the card just reviewed
 * @param randomFn injectable () => [0, 1) (defaults to Math.random)
 */
export function selectRandomCoveredConceptSource(
  cardsData: CardWithConcept[] | null | undefined,
  indexOfCurrentCard: number,
  randomFn: () => number = Math.random
): CoveredConceptSelection | null {
  if (!cardsData || cardsData.length === 0) return null;
  const lastCoveredIndex = Math.min(indexOfCurrentCard, cardsData.length - 1);
  if (lastCoveredIndex < 0) return null;

  const distinctConceptSelections: CoveredConceptSelection[] = [];
  const seen = new Set<string>();
  for (let cardIndex = 0; cardIndex <= lastCoveredIndex; cardIndex += 1) {
    const concept = (cardsData[cardIndex]?.card_key_concept_name || "").trim();
    if (concept && !seen.has(concept)) {
      seen.add(concept);
      distinctConceptSelections.push({ concept, sourceCardIndex: cardIndex });
    }
  }
  if (distinctConceptSelections.length === 0) return null;

  // If a checkpoint lands inside a passage, keep the quiz on that passage.
  // This makes its grounding coherent while retaining random concept choice
  // among the passage questions the learner has actually reached.
  const currentCard = cardsData[lastCoveredIndex];
  const currentAssessmentUnitId =
    currentCard?.assessment_unit_kind === "shared_stimulus" ? currentCard.assessment_unit_id : undefined;
  const currentPassageConceptSelections: CoveredConceptSelection[] = [];
  const currentPassageConceptsSeen = new Set<string>();
  if (currentAssessmentUnitId) {
    for (let cardIndex = 0; cardIndex <= lastCoveredIndex; cardIndex += 1) {
      const card = cardsData[cardIndex];
      if (card?.assessment_unit_id !== currentAssessmentUnitId) continue;
      const concept = (card.card_key_concept_name || "").trim();
      if (!concept || currentPassageConceptsSeen.has(concept)) continue;
      currentPassageConceptsSeen.add(concept);
      currentPassageConceptSelections.push({ concept, sourceCardIndex: cardIndex });
    }
  }
  const eligibleConceptSelections =
    currentPassageConceptSelections.length > 0 ? currentPassageConceptSelections : distinctConceptSelections;
  const pick = Math.floor(randomFn() * eligibleConceptSelections.length);
  // Guard against randomFn() === 1 (out of [0,1) contract) overrunning the array.
  return eligibleConceptSelections[Math.min(pick, eligibleConceptSelections.length - 1)];
}

export function selectRandomCoveredConcept(
  cardsData: CardWithConcept[] | null | undefined,
  indexOfCurrentCard: number,
  randomFn: () => number = Math.random
): string | null {
  return selectRandomCoveredConceptSource(cardsData, indexOfCurrentCard, randomFn)?.concept ?? null;
}

/**
 * Builds private challenge grounding from only the already-reached portion of
 * the selected passage. A checkpoint at question 3 of 5 includes questions
 * 1–3 and cannot leak questions 4–5.
 */
export function buildCoveredPassageChallengeContext(
  cardsData: CardData[] | null | undefined,
  indexOfCurrentCard: number,
  sourceCardIndex: number
): CoveredPassageChallengeContext {
  if (!cardsData || sourceCardIndex < 0 || sourceCardIndex >= cardsData.length) return {};
  const sourceCard = cardsData[sourceCardIndex];
  if (sourceCard.assessment_unit_kind !== "shared_stimulus" || !sourceCard.assessment_unit_id) return {};

  const lastCoveredIndex = Math.min(Math.max(indexOfCurrentCard, 0), cardsData.length - 1);
  const exploredPassageCards = cardsData
    .slice(0, lastCoveredIndex + 1)
    .filter((card) => card.assessment_unit_id === sourceCard.assessment_unit_id)
    .sort(
      (firstCard, secondCard) =>
        (firstCard.assessment_unit_question_index ?? Number.MAX_SAFE_INTEGER) -
        (secondCard.assessment_unit_question_index ?? Number.MAX_SAFE_INTEGER)
    );
  const exploredQuestions = exploredPassageCards
    .map((card): ExploredPassageQuestionContext | null => {
      const question = (card.questionVersionBeingUsed || card.question || "").trim().slice(0, 1200);
      if (!question) return null;
      const correctAnswer = (card.answerVersionBeingUsed || card.answer || card.answer_from_ai || "")
        .trim()
        .slice(0, 600);
      const learnerAnswer = (card.selectedAnswerText || "").trim().slice(0, 600);
      return {
        question,
        correctAnswer: correctAnswer || undefined,
        learnerAnswer: learnerAnswer || undefined,
      };
    })
    .filter((question): question is ExploredPassageQuestionContext => question !== null);

  return {
    passageContext: getAssessmentUnitPassageTextForTutor(sourceCard),
    exploredQuestions: exploredQuestions.length > 0 ? exploredQuestions : undefined,
  };
}
